import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, XCircle, Copy, ChevronDown, Search, RefreshCw, Plus, FileUp, ChevronLeft, ChevronRight, CheckSquare, Square, RotateCcw, Globe, FileText, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, appApi, authedFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* ---------- status model ---------- */
const norm = (s) => String(s || "").toLowerCase().replace(/-/g, "_");

const JOURNEY_GROUPS = [
  {
    label: "Shipment Booking",
    tabs: [
      { key: "new", label: "New", statuses: ["created", "pending_assignment"] },
      { key: "courier-assigned", label: "Courier Assigned", statuses: ["pickup_pending", "out_for_pickup", "pickup_scheduled"] },
      { key: "pickups-manifests", label: "Pickups & Manifests", statuses: ["manifested", "picked_up", "pickup_complete", "pickup_completed"] },
    ],
  },
  {
    label: "Shipment Journey",
    tabs: [
      { key: "in-transit", label: "In Transit", statuses: ["in_transit", "reached_hub"] },
      { key: "out-for-delivery", label: "Out For Delivery", statuses: ["out_for_delivery", "ofd"] },
      { key: "delivered", label: "Delivered", statuses: ["delivered"] },
    ],
  },
  {
    label: "NDR Exceptions",
    tabs: [
      { key: "ndr", label: "NDR", statuses: ["ndr"] },
      { key: "rto-in-transit", label: "RTO In-Transit", statuses: ["rto", "rto_in_transit"] },
      { key: "rto-delivered", label: "RTO Delivered", statuses: ["rto_delivered"] },
    ],
  },
];
const EXTRA_TABS = [
  { key: "all", label: "All", statuses: null },
  { key: "archive", label: "Archive", statuses: ["cancelled"] },
];
const ALL_TABS = [...JOURNEY_GROUPS.flatMap((g) => g.tabs), ...EXTRA_TABS];

const CHIP = {
  "New Order": "bg-emerald-100 text-emerald-700",
  Scheduled: "bg-sky-100 text-sky-700",
  "Picked Up": "bg-indigo-100 text-indigo-700",
  "In Transit": "bg-blue-100 text-blue-700",
  "Out For Delivery": "bg-amber-100 text-amber-700",
  Delivered: "bg-green-100 text-green-700",
  NDR: "bg-orange-100 text-orange-700",
  RTO: "bg-red-100 text-red-700",
  "RTO Delivered": "bg-red-100 text-red-700",
  Cancelled: "bg-zinc-200 text-zinc-500",
};
const statusChip = (s) => {
  const n = norm(s);
  if (["created", "pending_assignment"].includes(n)) return "New Order";
  if (["pickup_pending", "out_for_pickup", "pickup_scheduled"].includes(n)) return "Scheduled";
  if (["manifested", "picked_up", "pickup_complete", "pickup_completed"].includes(n)) return "Picked Up";
  if (["in_transit", "reached_hub"].includes(n)) return "In Transit";
  if (["out_for_delivery", "ofd"].includes(n)) return "Out For Delivery";
  if (n === "delivered") return "Delivered";
  if (n === "ndr") return "NDR";
  if (n === "rto_delivered") return "RTO Delivered";
  if (n.startsWith("rto")) return "RTO";
  if (n === "cancelled") return "Cancelled";
  return String(s || "—").replace(/[_-]/g, " ");
};
const statusText = (s) => statusChip(s);

const fmtOrderDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toUpperCase();
  return `${date} | ${time}`;
};
const volumetric = (p) => {
  const l = parseFloat(p?.length_cm), b = parseFloat(p?.breadth_cm), h = parseFloat(p?.height_cm);
  if (!l || !b || !h) return "0";
  return String(Math.round(((l * b * h) / 5000) * 100) / 100);
};
const typeChip = (o) => {
  const t = String(o.shipment_type || "").toLowerCase();
  if (t === "b2b") return "B2B";
  if (t === "air") return "AIR";
  if (t === "international") return "INTL";
  return "MPS";
};

/* TAT rules: WB/Bihar/Jharkhand 5 days · Assam & all other states 7 days */
const TAT5 = ["west bengal", "bihar", "jharkhand"];
const tatFor = (state) => (TAT5.includes(String(state || "").trim().toLowerCase()) ? 5 : 7);
const DAY_MS = 86400000;
const timingOf = (o) => {
  const created = new Date(o.created_at).getTime();
  const evs = o.events || [];
  const delEv = evs.find((e) => /delivered/i.test(`${e.status} ${e.description || ""}`) && !/rto/i.test(`${e.status} ${e.description || ""}`));
  const lastEv = evs[evs.length - 1];
  const endIso = (delEv || lastEv)?.timestamp || (delEv || lastEv)?.created_at;
  const st = norm(o.status);
  const delivered = st === "delivered";
  const closed = delivered || st === "cancelled" || st === "rto_delivered";
  const end = delivered && endIso ? new Date(endIso).getTime() : Date.now();
  const days = Math.max(0, Math.floor((end - created) / DAY_MS));
  const tat = tatFor(o.delivery?.state);
  return { days, tat, delivered, delayed: !closed && days > tat || (delivered && endIso && days > tat) };
};
const isDelayed = (o) => timingOf(o).delayed;

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [params] = useSearchParams();
  const [channel, setChannel] = useState("forward"); // forward | reverse
  const [domIntl, setDomIntl] = useState("domestic"); // domestic | international
  const [journey, setJourney] = useState(params.get("status") || "all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [payType, setPayType] = useState("");
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [menuFor, setMenuFor] = useState(null);
  const [labelBusy, setLabelBusy] = useState("");
  const [cancelBusy, setCancelBusy] = useState("");
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(() =>
    appApi.get("orders", search ? { search } : {}).then((d) => {
      setOrders(d.orders || []);
      setLastUpdated(new Date());
    }).catch(() => setOrders((o) => o ?? [])),
  [search]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { setPage(1); }, [journey, search, payType, dateFrom, dateTo, domIntl, channel, perPage, delayedOnly]);

  const delayedCount = useMemo(() => (orders || []).filter(isDelayed).length, [orders]);

  /* ---------- filtering ---------- */
  const counts = useMemo(() => {
    const c = { all: (orders || []).length };
    for (const t of ALL_TABS) {
      if (!t.statuses) continue;
      c[t.key] = (orders || []).filter((o) => t.statuses.includes(norm(o.status))).length;
    }
    return c;
  }, [orders]);

  const filtered = useMemo(() => {
    const tab = ALL_TABS.find((t) => t.key === journey) || ALL_TABS.find((t) => t.key === "all");
    return (orders || []).filter((o) => {
      if (channel === "reverse") return false; // no reverse pickups upstream
      const isIntl = String(o.shipment_type || "").toLowerCase() === "international";
      if (domIntl === "international" ? !isIntl : isIntl) return false;
      if (tab.statuses && !tab.statuses.includes(norm(o.status))) return false;
      if (delayedOnly && !isDelayed(o)) return false;
      if (payType && String(o.payment_mode || "").toLowerCase() !== payType) return false;
      if (dateFrom && new Date(o.created_at) < new Date(dateFrom)) return false;
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(o.created_at) > end) return false;
      }
      return true;
    });
  }, [orders, channel, domIntl, journey, payType, dateFrom, dateTo, delayedOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  /* ---------- actions ---------- */
  const copyAwb = (awb, e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(awb);
    toast.success(`AWB ${awb} copied.`);
  };

  const downloadLabel = async (o, e) => {
    e?.stopPropagation();
    setMenuFor(null);
    setLabelBusy(o.id);
    try {
      const res = await api.get(`/app/orders/${o.id}/label`, { responseType: "blob" });
      if ((res.headers["content-type"] || "").includes("pdf")) {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `label-${o.awb}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Label for ${o.awb} downloaded.`);
      } else {
        const text = JSON.parse(await res.data.text());
        toast.info(formatApiErrorDetail(text.detail));
      }
    } catch (err) {
      let msg = "Label not available yet.";
      if (err.response?.data instanceof Blob) {
        try { msg = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail); } catch { /* keep default */ }
      }
      toast.info(msg);
    } finally {
      setLabelBusy("");
    }
  };

  const requestCancel = async (o, e) => {
    e?.stopPropagation();
    setMenuFor(null);
    if (!window.confirm(`Request cancellation for ${o.awb}? Our team cancels it before courier pickup and the wallet debit is refunded automatically.`)) return;
    setCancelBusy(o.id);
    try {
      await appApi.post("tickets", {
        category: "shipment",
        subject: `URGENT: Cancel order ${o.awb}`,
        description: `Please cancel order ${o.awb} (${o.courier_display_name || o.courier_name}, ${o.pickup?.pincode} -> ${o.delivery?.pincode}) before courier assignment and refund the wallet debit of Rs.${o.rate ?? "-"}. Requested from the ShipZilla web panel.`,
      });
      toast.success(`Cancellation requested for ${o.awb} — refund follows on cancellation. Track it under Support.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelBusy("");
    }
  };

  /* ---------- tracking report (PDF / Excel) ---------- */
  const reportRows = (list) =>
    list.map((o) => {
      const t = timingOf(o);
      return {
        awb: o.awb,
        party: o.delivery?.name || "—",
        invoice: o.invoice_number || "—",
        courier: o.courier_display_name || o.courier_name || "—",
        route: `${o.pickup?.city || o.pickup?.pincode || "—"} → ${o.delivery?.city || o.delivery?.pincode || "—"}`,
        booked: fmtOrderDate(o.created_at).split(" | ")[0],
        status: statusChip(o.status),
        days: t.days,
        tat: `${t.tat}d`,
        delay: t.delayed ? (t.delivered ? `Delivered late +${t.days - t.tat}d` : `Delayed +${t.days - t.tat}d`) : "On track",
        delayed: t.delayed,
      };
    });

  const downloadReport = async (format) => {
    const list = chosenOrders;
    if (list.length === 0) return;
    setBulkBusy(format);
    try {
      const rows = reportRows(list);
      const summary = {
        total: rows.length,
        delayed: rows.filter((r) => r.delayed && r.delay.startsWith("Delayed")).length,
        late_delivered: rows.filter((r) => r.delay.startsWith("Delivered late")).length,
        on_track: rows.filter((r) => !r.delayed).length,
      };
      const res = await api.post("/reports/tracking", {
        format,
        client: user?.business || user?.name || user?.email || "",
        generated_at: new Date().toLocaleString("en-IN"),
        rows,
        summary,
      }, { responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipzilla-tracking-report-${Date.now()}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Tracking report (${ext.toUpperCase()}) downloaded — ${rows.length} shipment(s), ${summary.delayed} delayed.`);
    } catch (err) {
      let msg = "Report generation failed.";
      if (err.response?.data instanceof Blob) {
        try { msg = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail); } catch { /* keep */ }
      }
      toast.error(msg);
    } finally {
      setBulkBusy("");
    }
  };

  const goTrack = (o) => navigate(`/dashboard/tracking?awb=${encodeURIComponent(o.awb)}`);

  /* ---------- bulk labels + row ops ---------- */
  const chosenOrders = filtered.filter((o) => selected[o.id]);
  const toggleSel = (o, e) => {
    e.stopPropagation();
    setSelected((s) => ({ ...s, [o.id]: !s[o.id] }));
  };
  const toggleSelAll = (e) => {
    e.stopPropagation();
    const allOn = pageRows.every((o) => selected[o.id]);
    setSelected((s) => {
      const n = { ...s };
      pageRows.forEach((o) => { n[o.id] = !allOn; });
      return n;
    });
  };

  const bulkLabels = async (thermal) => {
    const ids = chosenOrders.map((o) => o.id);
    if (ids.length === 0) return;
    setBulkBusy(thermal ? "thermal" : "std");
    try {
      const res = await api.post(`/app/orders/${thermal ? "bulk-thermal-labels" : "bulk-labels"}`, { order_ids: ids }, { responseType: "blob" });
      if ((res.headers["content-type"] || "").includes("pdf")) {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `Shipzilla_Bulk_${thermal ? "Thermal_" : ""}${ids.length}_${Date.now()}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${ids.length} label(s) downloaded.`);
      } else {
        const text = JSON.parse(await res.data.text());
        toast.info(formatApiErrorDetail(text.detail));
      }
    } catch (err) {
      let msg = "Bulk labels failed.";
      if (err.response?.data instanceof Blob) {
        try { msg = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail); } catch { /* keep default */ }
      }
      toast.error(msg);
    } finally {
      setBulkBusy("");
    }
  };

  const refreshStatus = async (o, e) => {
    e?.stopPropagation();
    setMenuFor(null);
    try {
      const d = await appApi.get(`orders/${o.id}/tracking`);
      toast.success(`Status refreshed${d.status ? `: ${String(d.status).replace(/[_-]/g, " ")}` : "."}`);
      load();
    } catch (err) {
      toast.info(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const requestReturn = async (o, e) => {
    e?.stopPropagation();
    setMenuFor(null);
    if (!window.confirm(`Create a reverse pickup for ${o.awb}? The courier will collect it back from the customer.`)) return;
    try {
      await appApi.post(`orders/${o.id}/return`);
      toast.success(`Return requested for ${o.awb}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCustomsInvoice = async (o, e) => {
    e?.stopPropagation();
    setMenuFor(null);
    try {
      const d = await appApi.get(`orders/${o.id}/customs-invoice`);
      const url = d.url || d.customs_invoice_url;
      if (url) window.open(url, "_blank");
      else toast.info("Customs invoice not generated yet.");
    } catch (err) {
      toast.info(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="orders-page" className="space-y-4">
      {/* header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Orders</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Shipments</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} data-testid="orders-refresh" title="Refresh"
            className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-widest rounded-lg border border-zinc-300 bg-white px-3 py-2.5 hover:border-acid hover:text-acid transition-colors duration-150">
            <RefreshCw className="w-3.5 h-3.5" />
            Live{lastUpdated ? ` · ${lastUpdated.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}
          </button>
          <button onClick={() => navigate("/dashboard/add-order?tab=bulk")} data-testid="orders-bulk-import"
            className="inline-flex items-center gap-2 text-xs font-bold rounded-lg bg-obsidian text-white px-4 py-2.5 hover:bg-obsidian/90 transition-colors duration-150">
            <FileUp className="w-4 h-4" /> Bulk Import
          </button>
          <button onClick={() => navigate("/dashboard/add-order")} data-testid="orders-add-order"
            className="inline-flex items-center gap-2 text-xs font-bold rounded-lg bg-acid text-white px-4 py-2.5 hover:bg-acid/90 transition-colors duration-150">
            <Plus className="w-4 h-4" /> Add Order
          </button>
        </div>
      </div>

      {/* channel + domestic/international */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-5 border-b border-zinc-200" data-testid="orders-channel-tabs">
          {["forward", "reverse"].map((c) => (
            <button key={c} onClick={() => setChannel(c)} data-testid={`orders-channel-${c}`}
              className={`pb-2 text-sm font-semibold capitalize border-b-2 -mb-px transition-colors duration-150 ${
                channel === c ? "border-acid text-acid" : "border-transparent text-zinc-500 hover:text-obsidian"
              }`}>
              {c}
            </button>
          ))}
        </div>
        <div className="flex rounded-full border border-zinc-300 bg-white p-0.5" data-testid="orders-dom-tabs">
          {["domestic", "international"].map((d) => (
            <button key={d} onClick={() => setDomIntl(d)} data-testid={`orders-dom-${d}`}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full capitalize transition-colors duration-150 ${
                domIntl === d ? "bg-acid text-white" : "text-zinc-500 hover:text-obsidian"
              }`}>
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* journey tabs */}
      <div className="flex items-end gap-6 flex-wrap" data-testid="orders-journey-tabs">
        {JOURNEY_GROUPS.map((g) => (
          <div key={g.label}>
            <p className="text-[10px] font-semibold text-zinc-400 mb-1.5">{g.label}</p>
            <div className="flex rounded-lg border border-zinc-200 bg-white overflow-hidden">
              {g.tabs.map((t) => (
                <button key={t.key} onClick={() => setJourney(t.key)} data-testid={`orders-journey-${t.key}`}
                  className={`px-3.5 py-2 text-xs font-medium border-r border-zinc-200 last:border-r-0 transition-colors duration-150 ${
                    journey === t.key ? "bg-acid text-white" : "text-zinc-600 hover:bg-acid/10"
                  }`}>
                  {t.label}
                  {counts[t.key] != null && counts[t.key] > 0 && (
                    <span className={`ml-1.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${journey === t.key ? "bg-white/25 text-white" : "bg-red-100 text-red-600"}`}>
                      {counts[t.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div>
          <p className="text-[10px] font-semibold text-transparent mb-1.5 select-none">.</p>
          <div className="flex rounded-lg border border-zinc-200 bg-white overflow-hidden">
            {EXTRA_TABS.map((t) => (
              <button key={t.key} onClick={() => setJourney(t.key)} data-testid={`orders-journey-${t.key}`}
                className={`px-3.5 py-2 text-xs font-medium border-r border-zinc-200 last:border-r-0 transition-colors duration-150 ${
                  journey === t.key ? "bg-obsidian text-white" : "text-zinc-600 hover:bg-acid/10"
                }`}>
                {t.label}
                {t.statuses && counts[t.key] > 0 && (
                  <span className={`ml-1.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${journey === t.key ? "bg-white/25 text-white" : "bg-zinc-200 text-zinc-600"}`}>
                    {counts[t.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="orders-filters">
        <div className="flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 py-2">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="orders-date-from"
            className="text-xs text-zinc-600 outline-none bg-transparent" />
          <span className="text-xs text-zinc-400">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="orders-date-to"
            className="text-xs text-zinc-600 outline-none bg-transparent" />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }}
          className="flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-3 py-2 w-80">
          <Search className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search AWB / invoice no / order ID" data-testid="orders-search"
            className="text-xs outline-none bg-transparent w-full" />
          {search && (
            <button type="button" onClick={() => { setSearchInput(""); setSearch(""); }} aria-label="Clear search" data-testid="orders-search-clear"
              className="text-zinc-400 hover:text-red-600 transition-colors duration-100">
              <XCircle className="w-3.5 h-3.5" />
            </button>
          )}
        </form>
        <select value={payType} onChange={(e) => setPayType(e.target.value)} data-testid="orders-payment-filter"
          className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs text-zinc-600 outline-none">
          <option value="">Payment type</option>
          <option value="prepaid">Prepaid</option>
          <option value="cod">COD</option>
        </select>
        <button onClick={() => setDelayedOnly((v) => !v)} data-testid="orders-delayed-toggle"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
            delayedOnly ? "border-red-600 bg-red-600 text-white" : delayedCount > 0 ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100" : "border-zinc-300 bg-white text-zinc-500 hover:text-obsidian"
          }`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Delayed (TAT 5/7d)
          {delayedCount > 0 && <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${delayedOnly ? "bg-white/25 text-white" : "bg-red-600 text-white"}`}>{delayedCount}</span>}
        </button>
      </div>

      {/* bulk label toolbar */}
      {chosenOrders.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border-2 border-obsidian bg-obsidian text-white px-4 py-3 flex-wrap" data-testid="orders-bulk-toolbar">
          <p className="text-xs font-mono font-bold uppercase tracking-widest text-sky-300">{chosenOrders.length} selected</p>
          <button onClick={() => bulkLabels(false)} disabled={bulkBusy !== ""} data-testid="orders-bulk-labels"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest bg-acid text-white px-4 py-2 rounded-md hover:bg-acid/90 disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> {bulkBusy === "std" ? "Preparing…" : "Labels PDF"}
          </button>
          <button onClick={() => bulkLabels(true)} disabled={bulkBusy !== ""} data-testid="orders-bulk-thermal"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest bg-white text-obsidian px-4 py-2 rounded-md hover:bg-sky-100 disabled:opacity-50">
            <FileText className="w-3.5 h-3.5" /> {bulkBusy === "thermal" ? "Preparing…" : "Thermal 4x6 PDF"}
          </button>
          <button onClick={() => downloadReport("pdf")} disabled={bulkBusy !== ""} data-testid="orders-report-pdf"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50">
            <FileText className="w-3.5 h-3.5" /> {bulkBusy === "pdf" ? "Preparing…" : "Report PDF"}
          </button>
          <button onClick={() => downloadReport("xlsx")} disabled={bulkBusy !== ""} data-testid="orders-report-xlsx"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest bg-green-700 text-white px-4 py-2 rounded-md hover:bg-green-800 disabled:opacity-50">
            <FileSpreadsheet className="w-3.5 h-3.5" /> {bulkBusy === "xlsx" ? "Preparing…" : "Report Excel"}
          </button>
          <button onClick={() => setSelected({})} data-testid="orders-bulk-clear"
            className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white ml-auto">Clear</button>
        </div>
      )}

      {/* table */}
      <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto shadow-sm">
        <table className="w-full text-sm min-w-[1280px]" data-testid="orders-table">
          <thead>
            <tr className="bg-slate-50 border-b border-zinc-200 text-left">
              <th className="px-3 py-3 w-10">
                <button onClick={toggleSelAll} aria-label="Select all on page" data-testid="orders-select-all" className="text-obsidian align-middle">
                  {pageRows.length > 0 && pageRows.every((o) => selected[o.id]) ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-zinc-400" />}
                </button>
              </th>
              {["Order Date", "Order Details", "Product Details", "Package Details", "Payment", "Tracking Info", "Shipping Details", "Pickup Address", "Action"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((o) => {
              const chip = statusChip(o.status);
              return (
                <tr key={o.id || o.awb} onClick={() => goTrack(o)}
                  className="border-b border-zinc-100 last:border-0 hover:bg-acid/5 transition-colors duration-100 cursor-pointer align-top"
                  data-testid={`order-row-${o.awb}`}>
                  {/* select */}
                  <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                    <button onClick={(e) => toggleSel(o, e)} aria-label={`Select order ${o.awb}`} data-testid={`order-select-${o.awb}`} className="text-obsidian align-middle">
                      {selected[o.id] ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-zinc-400" />}
                    </button>
                  </td>
                  {/* Order Date */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className="text-xs text-zinc-700">{fmtOrderDate(o.created_at)}</p>
                    <p className="text-[11px] text-zinc-400 mt-1">Web</p>
                  </td>
                  {/* Order Details */}
                  <td className="px-4 py-4">
                    <p className="text-xs font-semibold text-acid">{o.external_order_id || o.awb}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">Ref. ID: --</p>
                    {o.invoice_number && (
                      <p className="text-[11px] text-zinc-500" data-testid={`order-invoice-${o.awb}`}>Inv: <span className="font-semibold text-zinc-700">{o.invoice_number}</span></p>
                    )}
                    <span className={`inline-block mt-1.5 text-[10px] font-semibold rounded-full px-2 py-0.5 ${CHIP[chip] || "bg-zinc-100 text-zinc-600"}`}
                      data-testid={`order-chip-${o.awb}`}>
                      {chip}
                    </span>
                  </td>
                  {/* Product Details */}
                  <td className="px-4 py-4">
                    <p className="text-xs text-zinc-700">{o.package?.description || "—"}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">SKU: --</p>
                    <p className="text-[11px] text-zinc-500">QTY: 1</p>
                  </td>
                  {/* Package Details */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className="text-xs text-zinc-700">
                      {o.package?.length_cm ? `${o.package.length_cm} x ${o.package.breadth_cm} x ${o.package.height_cm} (cm)` : "1 Boxe(s)"}
                      <span className="text-acid font-semibold"> - ({typeChip(o)})</span>
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-1">Dead wt. : {o.package?.weight_kg ?? "—"}kg</p>
                    <p className="text-[11px] text-zinc-500">Volumetric: {volumetric(o.package)}kg</p>
                  </td>
                  {/* Payment */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className="text-xs text-zinc-700">Invoice: ₹ {o.package?.invoice_value ?? "—"}</p>
                    <span className={`inline-block mt-1.5 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                      String(o.payment_mode).toLowerCase() === "cod" ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"
                    }`}>
                      {String(o.payment_mode || "prepaid").toUpperCase() === "COD" ? "COD" : "Prepaid"}
                    </span>
                  </td>
                  {/* Tracking Info */}
                  <td className="px-4 py-4">
                    <p className="text-xs font-semibold text-zinc-800">{o.courier_display_name || o.courier_name || "—"}</p>
                    <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
                      AWB: <span className="text-acid font-semibold">{o.awb}</span>
                      <button onClick={(e) => copyAwb(o.awb, e)} aria-label={`Copy AWB ${o.awb}`} data-testid={`copy-awb-${o.awb}`}
                        className="text-zinc-400 hover:text-acid transition-colors duration-100">
                        <Copy className="w-3 h-3" />
                      </button>
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">Status: {statusText(o.status)}</p>
                  </td>
                  {/* Shipping Details */}
                  <td className="px-4 py-4 max-w-[220px]">
                    <p className="text-xs font-semibold text-zinc-800">{o.delivery?.name || "—"}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{o.delivery?.phone || ""}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {[o.delivery?.address, o.delivery?.city, o.delivery?.state, o.delivery?.pincode].filter(Boolean).join(", ")}
                    </p>
                  </td>
                  {/* Pickup Address */}
                  <td className="px-4 py-4 max-w-[180px]">
                    <p className="text-xs text-zinc-700">{o.pickup?.name || "—"}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{[o.pickup?.city, o.pickup?.pincode].filter(Boolean).join(", ")}</p>
                  </td>
                  {/* Action */}
                  <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5 relative">
                      <button onClick={() => goTrack(o)} data-testid={`track-btn-${o.awb}`}
                        className="rounded-lg bg-acid text-white text-xs font-semibold px-4 py-2 hover:bg-acid/90 transition-colors duration-150">
                        Track
                      </button>
                      <button onClick={() => setMenuFor(menuFor === o.awb ? null : o.awb)} aria-label={`Actions for ${o.awb}`} data-testid={`order-actions-${o.awb}`}
                        className="rounded-lg bg-acid text-white p-2 hover:bg-acid/90 transition-colors duration-150">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      {menuFor === o.awb && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setMenuFor(null)} />
                          <div className="absolute right-0 top-full mt-1 z-50 w-52 rounded-lg border border-zinc-200 bg-white shadow-lg py-1" data-testid={`order-menu-${o.awb}`}>
                            <button onClick={(e) => refreshStatus(o, e)} data-testid={`refresh-status-${o.awb}`}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10">
                              <RefreshCw className="w-3.5 h-3.5" /> Refresh courier status
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setMenuFor(null); navigator.clipboard?.writeText(`${window.location.origin}/track/${o.awb}`); toast.success("Share link copied — send it to your customer."); }}
                              data-testid={`share-link-${o.awb}`}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10">
                              <Copy className="w-3.5 h-3.5" /> Copy customer tracking link
                            </button>
                            {o.goods_invoice && (
                              <button onClick={(e) => { e.stopPropagation(); setMenuFor(null); window.open(authedFileUrl(`orders/${o.id}/goods-invoice`), "_blank"); }}
                                data-testid={`goods-invoice-${o.awb}`}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10">
                                <FileUp className="w-3.5 h-3.5" /> Goods invoice{o.invoice_number ? ` #${o.invoice_number}` : ""}
                              </button>
                            )}
                            {(o.pods || []).filter((p) => !p.expired && !(p.expires_at && new Date(p.expires_at) < new Date())).map((p, i) => (
                              <button key={p.id || i} onClick={(e) => { e.stopPropagation(); setMenuFor(null); window.open(authedFileUrl(`pod/order/${o.id}/${p.id}`), "_blank"); }}
                                data-testid={`pod-${o.awb}-${i}`}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10">
                                <FileText className="w-3.5 h-3.5" /> POD {(o.pods || []).length > 1 ? i + 1 : ""} — proof of delivery
                              </button>
                            ))}
                            <button onClick={(e) => downloadLabel(o, e)} disabled={labelBusy === o.id} data-testid={`label-download-${o.awb}`}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10 disabled:opacity-50">
                              <Download className="w-3.5 h-3.5" /> {labelBusy === o.id ? "Preparing…" : o.label_url || o.has_admin_label ? "Download Label (PDF)" : "Request Label (PDF)"}
                            </button>
                            {String(o.shipment_type || "").toLowerCase() === "international" && (
                              <button onClick={(e) => openCustomsInvoice(o, e)} data-testid={`customs-invoice-${o.awb}`}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10">
                                <Globe className="w-3.5 h-3.5" /> Customs invoice
                              </button>
                            )}
                            {norm(o.status) === "delivered" && (
                              <button onClick={(e) => requestReturn(o, e)} data-testid={`return-order-${o.awb}`}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-zinc-700 hover:bg-acid/10">
                                <RotateCcw className="w-3.5 h-3.5" /> Request return pickup
                              </button>
                            )}
                            {norm(o.status) === "pending_assignment" && (
                              <button onClick={(e) => requestCancel(o, e)} disabled={cancelBusy === o.id} data-testid={`cancel-order-${o.awb}`}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50">
                                <XCircle className="w-3.5 h-3.5" /> {cancelBusy === o.id ? "Requesting…" : "Request Cancellation"}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {orders && pageRows.length === 0 && (
          <p className="px-5 py-12 text-sm text-zinc-500 text-center" data-testid="orders-empty">
            {channel === "reverse" ? "No reverse pickups yet." : "No orders for this filter."}
          </p>
        )}
        {!orders && <p className="px-5 py-12 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400 text-center">Loading orders…</p>}
      </div>

      {/* pagination */}
      {orders && filtered.length > 0 && (
        <div className="flex items-center justify-end gap-3 text-xs text-zinc-500" data-testid="orders-pagination">
          <span>Data per page:</span>
          <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} data-testid="orders-per-page"
            className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-xs outline-none">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</span>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page" data-testid="orders-prev-page"
            className="p-1.5 rounded-md border border-zinc-300 bg-white disabled:opacity-40 hover:border-acid hover:text-acid transition-colors duration-100">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page" data-testid="orders-next-page"
            className="p-1.5 rounded-md border border-zinc-300 bg-white disabled:opacity-40 hover:border-acid hover:text-acid transition-colors duration-100">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
